<script setup lang="ts">
import { computed, ref, useCssModule, watch } from "vue";

import BackStepButton from "@/components/BackStepButton/BackStepButton.vue";
import Modal from "@/components/modal/modal.vue";
import StepsButtons from "@/components/StepsButtons/StepsButtons.vue";
import globalClasses from "@/css/global-classes.module.css";
import { deviceGradeNormalize } from "@/helpers/deviceGrade";
import { apiGetQuestions } from "@/hooks/apiGetQuestions/apiGetQuestions";
import useTradeInCalculation from "@/hooks/useTradeInCalculation/useTradeInCalculation";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import { useHowYouRateDeviceStore } from "@/stores/howYouRateDevice/howYouRateDevice";
import { GRADES_DATA } from "@/stores/howYouRateDevice/howYouRateDevice.model";
import { I_HowYouRateDevice } from "@/stores/howYouRateDevice/howYouRateDevice.types";
import { useTIWStore } from "@/stores/tiw/tiw";
import Button from "@/ui/button/button.vue";
import { RadioSkeleton } from "@/ui/skeletons";

const classes = useCssModule();

const howYouRateDeviceStore = useHowYouRateDeviceStore();

const prevFlow = ref<I_HowYouRateDevice["questionData"][]>([]);
const questionData = ref<I_HowYouRateDevice["questionData"]>(
    howYouRateDeviceStore.discountLaterFirstQuestion,
);
const answers = ref<string[]>([]);
const isLoading = ref<boolean>(false);

const TIWStore = useTIWStore();

const isMultiSelect = computed(() => {
    return questionData.value.answerType === "MULTISELECT";
});

const isYesNoQuestion = computed(() => {
    return questionData.value.answerType === "YN";
});

watch(answers, () => console.log(answers.value), { deep: true });

const setAnswer = (event: Event) => {
    const target = event.target as HTMLInputElement;

    if (answers.value.includes(target.value)) {
        answers.value = answers.value.filter((el) => el !== target.value);
        return;
    }
    if (isMultiSelect.value) {
        answers.value.push(target.value);
    } else {
        answers.value = [];
        answers.value.push(target.value);
    }
};

function onBack() {
    const prev = prevFlow.value.pop();
    if (!prev) {
        TIWStore.switchStep("howYouRateDevice");
        return;
    }

    questionData.value = prev;
}

const getQuestionData = async (payload: { ans: string; c: string }) => {
    isLoading.value = true;
    if (questionData.value) prevFlow.value.push(questionData.value);

    questionData.value = await apiGetQuestions({
        ...payload,
        deviceId: howYouRateDeviceStore.registeringDeviceData.deviceId,
        lang: TIWStore.standardizedLocale,
        questionId: questionData.value?.questionId ?? "",
        token: TIWStore.WIDGET_DATA.token,
    });
    isLoading.value = false;
};

async function onNext(yesNoAnswer?: "Y" | "N") {
    const joinedAnswer = answers.value.join();
    if (questionData.value.last) {
        await getQuestionData({ c: joinedAnswer, ans: yesNoAnswer });
        TIWStore.switchStep("successfulEvaluation");
        howYouRateDeviceStore.grade = deviceGradeNormalize(questionData.value.device.grade);
        howYouRateDeviceStore.rate = useTranslate(
            `tiw__grades__${GRADES_DATA[questionData.value.device.grade].gradeCode}_stateInfo`,
        );
        useTradeInCalculation({
            tradeInPrice: questionData.value.device.price,
        });
        return;
    }
    await getQuestionData({ c: joinedAnswer, ans: yesNoAnswer });
    answers.value = [];
}
</script>

<template>
    <!-- TODO переводы -->
    <Modal>
        <template #header>
            <!-- TODO перевод -->
            <p class="tiw__h2">Oceń stan urządzenia</p>
        </template>
        <div v-if="questionData" :class="classes.questionsContainer">
            <p :class="classes.title">
                <b>{{ questionData.question }}</b>
            </p>
            <p :class="classes.desc" v-html="questionData.description" />
            <div v-if="questionData.options" :class="classes.options">
                <div v-for="option in questionData.options" :key="option.cid" :class="classes.item">
                    <input
                        :id="option.cid"
                        :class="classes.input"
                        :value="option.cid"
                        type="checkbox"
                        :checked="answers.includes(option.cid)"
                        name="options"
                        @change="setAnswer"
                    />
                    <label :class="classes.label" :for="option.cid">
                        <span :class="classes.cycle">
                            <div :class="classes.dot"></div>
                        </span>
                        <div>
                            {{ option.cname }}
                            <div class="desc" v-html="option.description" />
                        </div>
                    </label>
                </div>
            </div>

            <!-- TODO перевод -->
            <StepsButtons
                :loading="isLoading"
                :disabled="isYesNoQuestion ? isLoading : !answers.length || isLoading"
                :btn-text="isYesNoQuestion ? 'Tak' : useTranslate('tiw__continue')"
                :click-to-next="isYesNoQuestion ? () => onNext('Y') : () => onNext()"
            >
                <template #backStepBtn="{ className }">
                    <BackStepButton :class="className" @click="onBack" />
                </template>

                <template #additionalBtn>
                    <Button
                        v-if="isYesNoQuestion"
                        :class="globalClasses.additionalButton"
                        variant="basic"
                        color="light"
                        :disabled="isYesNoQuestion ? isLoading : !answers.length || isLoading"
                        @click="isYesNoQuestion ? onNext('N') : onNext()"
                    >
                        <!-- TODO перевод -->
                        Nie
                    </Button>
                </template>
            </StepsButtons>
        </div>
        <div v-else class="tiw__visualEvaluation__skeletons">
            <RadioSkeleton :count="8" />
        </div>
    </Modal>
</template>

<style module>
.questionsContainer {
    --margin: 16px;
}
.title {
    margin: 0;
    margin-bottom: var(--margin) !important;
}
.desc {
    margin: 0;
    margin-bottom: var(--margin);

    &:has(li) {
        padding-left: 20px;
    }
}
.options {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
}
.item {
    display: flex;
    align-items: start;
    gap: 12px;

    &:has(input:checked) {
        .dot {
            width: 12px;
            height: 12px;
            background: var(--custom-color-main);
            border-radius: 100%;
        }
    }
}
.input {
    width: 0px;
    margin: 0;
    flex-shrink: 0;
    height: 0px;
}
.label {
    font-size: 14px;
    display: flex;
    align-items: start;
    gap: 12px;
}
.cycle {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 1px solid #808d9a;
    border-radius: 100%;
    flex-shrink: 0;
}
.dot {
    transition: all 0.3s;
    width: 0;
    height: 0;
}
.desc {
    color: #808d9a;
    font-size: 12px;

    &:deep(ul) {
        padding: 0;
        padding-left: 25px;
        margin-bottom: 0;
        margin-top: 8px;
    }
}
</style>
